import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import useCurrentUser from 'src/hooks/use-current-user';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { paths } from 'src/paths';
import { addSignOnBehalfAction, getTherapistInfoAction, resetNewDocumentSignAction, resetSignOnBehalfAction } from 'src/redux/actions/documentationAction';
import { getAccessToken } from 'src/routes/AuthContext';
import { canDo, SignatureAction } from 'src/types/signatureContext';
import { resolveProxySignNavState } from './compliance-sign-on-behalf';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import SignDocementModel from './signature-document-popup';
import SignOnBehalfModal from './signature-on-behalf-modal';

function SignatureTemplatePreviewNew() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { document } = location?.state;
  // const [keys, setKeys] = useState({
  //   documentId: '',
  //   embedKey: '',
  // });
  const { patients, patientDetails, episodeOfCare, details } = paths;
  // const signatureTemplateResponse = useSelector((state: any) => state.getSignatureTemplateReducer);
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const signOnBehalf = useSelector((state: any) => state.addSignOnBehalfReducer)
  const [providerId] = useCurrentUser(['providerId']);
  const [iframeRef, setIframeRef] = useState<any | null>(null);

  // THIN-7346: Derive the signing axis from the BE-computed signatureContext, not from
  // `providerId === document.supervisorId`. The therapist and supervisor "Sign document" links
  // both navigate here with identical state, so the intended role isn't carried in nav state and
  // must be re-derived. The raw ID compare misfires when the SAME provider is both the therapist
  // and the supervisor: it returns true on the therapist-first flow, so the signature is wrongly
  // submitted as a supervisor cosign and the BE rejects it. The therapist axis takes precedence —
  // the supervisor cosign axis only opens (CAN_SIGN) once the therapist signature is applied — so
  // check therapist-first. Fall back to the legacy compare only when signatureContext is absent.
  //  BRK-2059: the `isProxySignature` nav-state key carries either the legacy DOR string
  //  ('PROXY_SIGNATURE') or a typed compliance ProxySignNavState; normalize once. Null = a
  //  direct (non-proxy) signing navigation.
  const proxySign = resolveProxySignNavState(location?.state?.isProxySignature, document);

  const signatureCtx = document?.signatureContext;
  const isSupervisorSignature = signatureCtx
    ? canDo(signatureCtx.allowedSupervisorSignatureActions, SignatureAction.CAN_SIGN) &&
      !canDo(signatureCtx.allowedTherapistSignatureActions, SignatureAction.CAN_SIGN)
    : providerId === document?.supervisorId;

  const [openNewSignModel, setOpenNewSignModel] = useState(false);
  const [isDocSigned, setIsDocSigned] = useState({
    isSuccessful: false,
    pdfUrl: '',
  });

  const [openSignOnBehalfModal, setOpenSignOnBehalfModal] = useState(false);

  const updateIFrameRef = useCallback((node: any) => {
    setIframeRef(node);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetSignOnBehalfAction())
    }
  }, [])

  useEffect(() => {
    const iframeElement = iframeRef;

    if (iframeElement) {
      const interval = setInterval(async () => {
        const response = await getAccessToken();
      }, 15000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [iframeRef]);

  const goBackToEOC = () => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: location?.state?.episodeOfCareId,
        getPatientId,
      },
    });
  };

  const handleOpenNewSignModel = () => {
    if (proxySign) {
      if (isDocSigned.isSuccessful) {
        navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
          state: {
            episodeOfCareId: location?.state?.episodeOfCareId,
            getPatientId,
          },
        });
        return
      } else {
        dispatch(resetSignOnBehalfAction())
        //  The on-behalf card: the treating therapist, or (compliance supervisor cosign) the
        //  assigned supervisor — supervisors are providers, so the same lookup serves both.
        dispatch(getTherapistInfoAction({
          providerId: proxySign.onBehalfOfProviderId
        }))
        setOpenSignOnBehalfModal(true);
        return
      }
    }
    if (isDocSigned.isSuccessful) {
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
        state: {
          episodeOfCareId: location?.state?.episodeOfCareId,
          getPatientId,
        },
      });
    } else {
      dispatch(resetNewDocumentSignAction());
      setOpenNewSignModel(true);
    }
  };

  const handleCloseNewSignModel = () => {
    setOpenNewSignModel(false);
    dispatch(resetNewDocumentSignAction());
  };

  const handleSignOnBehalfSubmit = (
    document: any,
    signature: string,
    reason: string,
    consentConfirmation: boolean,
    pin?: string
  ) => {
    dispatch(addSignOnBehalfAction({
      formSubmissionUuid: document?.uuid,
      signerName: signature,
      consentConfirmation,
      //  BRK-2059: the compliance flow can satisfy the SUPERVISOR slot; the backend maps
      //  isSupervisor + isProxySignature to PROXY_FOR_SUPERVISOR (BRK-2057).
      isSupervisor: proxySign?.axis === 'SUPERVISOR',
      documentType: document?.formType?.replaceAll(' ', '_')?.toUpperCase(),
      //  Always the CALLER's own provider id — the backend verifies it against the JWT.
      dorUuid: providerId,
      isProxySignature: true,
      proxySignReason: reason,
      //  THIN-7775: the service layer moves this into the X-Signing-Pin header.
      ...(pin ? { pin } : {}),
    }))
  };

  const [popUpMessage, setPopUpMessage] = useState({ type: '', message: '' });

  useEffect(() => {
    if (signOnBehalf?.response === null && !signOnBehalf?.isSignOnBehalf) return;
    if (signOnBehalf?.response !== null && !signOnBehalf?.isSignOnBehalf) {
      //  THIN-7706/BRK-2059: a structured PIN challenge is handled inline by the modal
      //  (wrong pin / lockout / no pin set) — closing it here would throw the retry away.
      const outcome = signOnBehalf?.response?.data?.outcome;
      if (outcome === 'LOCKED' || outcome === 'WRONG_PIN' || outcome === 'NO_PIN_SET') {
        return;
      }
      setOpenSignOnBehalfModal(false)
      //  ERROR OCCURRED
      setPopUpMessage({ message: signOnBehalf?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });

      }, 3000);

      return;
    }
    setPopUpMessage({ message: signOnBehalf?.response?.message, type: '' });
    setIsDocSigned({
      isSuccessful: signOnBehalf?.response?.isSuccessful,
      pdfUrl: signOnBehalf?.response?.pdfUrl,
    });
    setOpenSignOnBehalfModal(false)
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
    }, 3000);
  }, [signOnBehalf]);



  return (
    <>
      <Stack
        gap="1.5rem"
        sx={{ padding: '0 4rem' }}
      >
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
          onClick={goBackToEOC}
        >
          <Typography variant="subtitle1">
            Back to Episode Of Care Details -
            {` ${location?.state?.patientData?.lastName ?? ''} ${
              location?.state?.patientData?.firstName ?? ''
            }`}
          </Typography>
        </Button>
        <Stack
          flexDirection="row"
          gap="1rem"
          alignItems="center"
          justifyContent={'space-between'}
        >
          <Typography variant="h5">
            Preview Document - {location?.state?.document?.documentType}
          </Typography>
          <Button
            variant="contained"
            onClick={handleOpenNewSignModel}
          >
            {isDocSigned.isSuccessful ? 'Submit' : 'Start signing'}
          </Button>
        </Stack>
      </Stack>
      <Box
        sx={{ height: '100%' }}
        p={3}
      >
        <iframe
          src={
            isDocSigned.isSuccessful ? isDocSigned.pdfUrl : `${location?.state?.document?.pdfUrl}`
          }
          ref={updateIFrameRef}
          width={'100%'}
          height={'100%'}
          style={{ border: 'none' }}
          title='Documents'
        />
      </Box>

      <BootstrapDialog
        PaperProps={{ style: { width: '600px' } }}
        onClose={handleCloseNewSignModel}
        aria-labelledby="customized-dialog-title"
        open={openNewSignModel}
      >
        <SignDocementModel
          onClose={handleCloseNewSignModel}
          setIsDocSigned={setIsDocSigned}
          formSubmissionUuid={document?.uuid}
          isSupervisor={isSupervisorSignature}
          docType={document?.formType}
          open
        />
      </BootstrapDialog>
      {/* Sign on Behalf Modal */}
      <BootstrapDialog
        onClose={() => setOpenSignOnBehalfModal(false)}
        aria-labelledby="customized-dialog-title"
        open={openSignOnBehalfModal}
      >
        <SignOnBehalfModal
          onClose={() => setOpenSignOnBehalfModal(false)}
          onSign={handleSignOnBehalfSubmit}
          signOnBehalfDoc={location?.state?.document}
          onBehalfOfLabel={proxySign?.axis === 'SUPERVISOR' ? 'Supervisor' : 'Therapist'}
          signerLabel={proxySign?.source === 'COMPLIANCE' ? 'Signer' : 'DOR'}
        />
      </BootstrapDialog>
      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}
    </>
  );
}

export default SignatureTemplatePreviewNew;
